import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Run A, back property line: 96 linear ft, 6 ft vinyl privacy, white, 5 in posts', amount: '$4,320' },
  { item: 'Run B, left side to the house: 54 linear ft, 6 ft vinyl privacy', amount: '$2,430' },
  { item: 'Double drive gate, 10 ft, with drop rod and gravity latch', amount: '$1,150' },
  { item: 'Optional: gothic post caps on all posts, 24 caps', amount: '$190' },
];

const faq = [
  {
    q: 'Why does the template list the fence run by run?',
    a: 'Because runs differ: a side run may be shorter, on a slope, or a different height for a neighbor. Separate runs make the estimate easy to adjust if the customer drops or shortens one.',
  },
  {
    q: 'What does the property-line section say?',
    a: 'It asks how the line is being set: by a survey, by pins the homeowner shows you, or by the homeowner\'s instruction. Filling it in before you dig avoids the most expensive kind of fence dispute.',
  },
  {
    q: 'Is the template free?',
    a: 'Yes. Read it here, download the PDF or copy it into your own documents. No sign-up.',
  },
  {
    q: 'Can I send it for an online signature?',
    a: 'Build the same estimate in Scopie and it is emailed to the homeowner as a link. They can tick extras such as post caps and sign on their phone.',
  },
];

const page: TemplatePage = {
  slug: 'fence-estimate-template',
  path: '/templates/fence-estimate-template',
  primaryKeyword: 'fence estimate template',
  secondaryKeywords: ['fence quote template', 'fence installation estimate form', 'fencing proposal template'],
  // no "(PDF + ...)" group on purpose: withLeadCopy (template-lead.ts) rewrites that group to "(PDF + Google Sheet)" when the lead form is on, which would drop "Sample"
  title: 'Free Fence Estimate Template and Sample (PDF) | Scopie', // 54
  metaDescription:
    'A free fence estimate template with a run-by-run table, gate schedule, tear-out line, and property-line and utility sections, plus a filled sample.', // 148
  h1: 'Fence estimate template',
  answerFirst:
    'A fence estimate should break the job into runs, give each its length, height and style, price gates separately, and settle the property line and utility marking before anyone digs. This free template is built around that run-by-run table, with a gate schedule, tear-out line and a filled-in vinyl sample.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'callout',
      id: 'line',
      text: 'Fill in the property-line section first. It is the part of a fence estimate that prevents the costliest arguments.',
    },
    {
      type: 'document',
      id: 'template',
      h2: 'The fence estimate',
      intro: 'One line per run; add or delete runs as needed.',
      pdfName: 'fence-estimate-template',
      sections: [
        { heading: 'Fence company', lines: ['[Company] · [Phone] · [Email] · [License or registration, if required]'] },
        { heading: 'Customer', lines: ['[Name] · [Address]', 'Estimate [number] · [date] · valid until [date]'] },
        {
          heading: 'Fence runs',
          lines: [
            'Run [A]: [linear ft] · height [ft] · style [privacy / picket / split rail / chain link / ornamental] · material [wood / vinyl / aluminum / steel] · [price]',
            'Run [B]: [linear ft] · height [ft] · style [ ] · material [ ] · [price]',
            'Posts: [size and type], set in [concrete / gravel], spacing [ft]',
            'Slope handling: [stepped / racked / none needed]',
          ],
        },
        { heading: 'Gates', lines: ['[Walk / drive / double] gate, [width], hardware [hinges, latch, drop rod] · [price]'] },
        { heading: 'Removal', lines: ['Remove and dispose of [length] of existing [type] fence · [price]'] },
        {
          heading: 'Property line, permits and utilities',
          lines: ['Line set by: [survey / pins shown by owner / owner\'s instruction]; contractor does not determine boundaries.', 'Permit or HOA approval obtained by [contractor / owner].', 'Utility locate requested by [contractor / owner] on [date].'],
        },
        { heading: 'Terms', lines: ['Rock, roots or buried obstacles: work stops and the extra is priced before continuing.', 'Deposit: [amount], due on acceptance, before materials are ordered.', 'Cancellation: [your terms].'] },
        { heading: 'Acceptance', lines: ['[Customer name] · [Signature] · [Date]'] },
      ],
    },
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'Example: a vinyl privacy fence with a drive gate',
      label: 'Filled in for a corner lot',
      lines: filled,
      total: '$8,090',
      totalLabel: 'Total with the post caps',
      note: 'Illustrative prices for one job.',
    },
    {
      type: 'list',
      id: 'fill-in',
      h2: 'How to fill in the runs',
      style: 'steps',
      items: [
        { title: 'Walk the line', text: 'Measure each straight section between corners or ends and give it a letter.' },
        { title: 'Note the grade', text: 'Mark runs on a slope and decide between stepping and racking.' },
        { title: 'Place the gates', text: 'Write the width and which way each gate swings, especially for mowers and vehicles.' },
        { title: 'Check what comes out', text: 'Old fence, concrete footings and overgrowth along the line all take time.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'What fence estimates usually leave out',
      style: 'mistakes',
      items: [
        { title: 'Gate hardware', text: 'Heavy drive gates need stronger posts, drop rods and sometimes a wheel. Price the hardware, not just the gate leaf.' },
        { title: 'Concrete cure time', text: 'Homeowners want to know when the dog can go out. Tell them how long before the fence takes weight.' },
        { title: 'Material lead time', text: 'Vinyl and aluminum can take weeks in spring. Put the expected wait on the estimate so the install date is realistic.' },
        { title: 'Which side faces out', text: 'Neighbors and HOAs often require the finished side outward. Settle it on paper.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Fence estimate template questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/fencing', anchor: 'Scopie for fence contractors', status: 'live' },
    { href: '/templates/landscaping-quote-template', anchor: 'landscaping quote template', status: 'live' },
    { href: '/templates/cancellation-policy', anchor: 'cancellation policy template', status: 'live' },
  ],
  cta: {
    heading: 'Turn the run table into a signed estimate',
    text: 'Save your fence styles, gates and clauses in Scopie, then email each estimate as a link from the yard.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
